import React, { useEffect, useRef, useState } from "react";
import { Card, Container, Spinner } from "reactstrap";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import BroadcastsTable from "./BroadcastsTable";
import DeleteModal from "../../../modals/DeleteModal";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { useTranslation } from "context/TranslationProvider";

const AllBroadcasts = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const notifyRef = useRef(notify);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState(null);
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const res = await userServices.fetchBroadcasts();
        const data = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
        if (isMounted) setItems(data);
      } catch (err) {
        const backendError = err?.response?.data?.messageKey || err?.response?.data?.error;
        const backendMessage = err?.response?.data?.message;
        notifyRef.current(
          "error",
          t(backendError, backendMessage || t("broadcasts.toast.fetchFailed", "Failed to fetch broadcasts"))
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    const interval = setInterval(fetchData, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [t, notify]);

  const handleDelete = () => {
    if (!deleteTarget?._id) return;

    userServices
      .deleteBroadcast(deleteTarget._id)
      .then((res) => {
        notify("success", t(res?.data?.messageKey, res?.data?.message || t("broadcasts.toast.deleteSuccess", "Broadcast deleted successfully")));
        setItems((prev) => prev.filter((b) => b._id !== deleteTarget._id));
      })
      .catch((error) => {
        const backendError = error?.response?.data?.messageKey || error?.response?.data?.error;
        const backendMessage = error?.response?.data?.message;
        notify(
          "error",
          t(backendError, backendMessage || t("broadcasts.toast.deleteFailed", "Failed to delete broadcast"))
        );
      })
      .finally(() => setDeleteTarget(null));
  };

  const handleSendBroadcast = async (row) => {
    try {
      const res = await userServices.sendBroadcast(row._id);

      const count = res.data?.totalRecipients ?? 0;

      notify("success", `${t(res?.data?.messageKey, res?.data?.message || t("broadcasts.toast.sentSuccess", "Broadcast sent successfully"))} (${count})`);

      if (res.data?.broadcast) {
        setItems((prev) =>
          prev.map((b) =>
            b._id === row._id
              ? {
                  ...b,
                  ...res.data.broadcast,
                  createdBy: b.createdBy,
                }
              : b
          )
        );
      }
    } catch (error) {
      const backendError = error?.response?.data?.messageKey || error?.response?.data?.error;
      const backendMessage = error?.response?.data?.message;
      notify(
        "error",
        t(backendError, backendMessage || t("broadcasts.toast.sentFailed", "Failed to send broadcast"))
      );
    }
  };

  const handleStatusChange = async (row, nextStatus) => {
    try {
      const res = await userServices.updateBroadcast(row._id, { status: nextStatus });

      notify(
        "success",
        t(res?.data?.messageKey, res?.data?.message || t("broadcasts.toast.updateSuccess", "Broadcast updated successfully"))
      );

      setItems((prev) =>
        prev.map((b) =>
          b._id === row._id
            ? { ...b, status: nextStatus }
            : b
        )
      );
    } catch (error) {
      const backendError = error?.response?.data?.messageKey || error?.response?.data?.error;
      const backendMessage = error?.response?.data?.message;
      notify(
        "error",
        t(backendError, backendMessage || t("broadcasts.toast.updateFailed", "Failed to update broadcast"))
      );
    }
  };

  const renderLoading = () => (
    <div className="center-loader">
      <div className="center-loader-content">
        <Spinner/>
        <p className="mt-3 text-muted">
          {t("common.loading", "Loading....")}
        </p>
      </div>
    </div>
  );

  return (
    <div className="card-header-full-height">
      <SimpleHeader
        name={t("sideNav.broadCasts", "Broadcasts")}
        parentName={t("sideNav.dashboard", "Dashboard")}
      />

      <Container className="mt--6" fluid>
        <Card className="shadow">
          {loading ? (
            renderLoading()
          ) : (
            <BroadcastsTable
              items={items}
              deleteRow={(row) => setDeleteTarget(row)}
              onSend={handleSendBroadcast}
              onStatusChange={handleStatusChange}
            />
          )}
        </Card>
      </Container>

      <DeleteModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        itemName="broadcast"
        onDelete={handleDelete}
      />
    </div>
  );
};

export default AllBroadcasts;
